import React, { FC, useEffect } from 'react';
import StepWizard, { StepWizardChildProps } from 'react-step-wizard';
import { useDispatch } from 'react-redux';
import { useStateMachine } from 'little-state-machine';
import { Grid, GridCol } from '@naf/grid';
import { TextVariant } from '@naf/text';
import { LayoutWithMainContent } from '@components/layout/Layout';
import { useAuth0Token } from '@hooks/useAuth0Token';
import { actions as IdpActions } from '@redux/modules/internationalDrivingPermit';
import { actions as idpCountriesAction } from '@redux/modules/idpCountries';
import { useFetchProfile } from '@hooks/useFetchProfile';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { useDocumentData } from '@hooks/Documents/useDocumentData';
import { ProductArticleType } from '@appTypes/articleType';
import { CompareMembershipBlockType } from '@appTypes/CompareMembershipBlockType';
import { DynamicForm } from '@components/block-content/types/components/Form/FormBlock.types';
import { Progressbar } from '../BecomeAMember/components/ProgressBar';
import { updateAction } from './lib/updateAction';
import { Redirect } from './components/Redirect/Redirect';
import { DrivingPermit } from './forms/drivingPermit/DrivingPermit';
import { IdpConsents } from './forms/consents/Consents';
import { Holder } from './forms/holder';
import { DocumentUpload } from './components/DocumentUpload/DocumentUpload';
import { IDP_ARTICLE_SLUG } from './components/Redirect/Redirect.utils';
import * as S from './InternationalDrivingPermit.styles';

interface Step {
  step: FC<Partial<StepWizardChildProps>> | string;
  name: string;
  type: 'FORM' | 'REDIRECT';
}

export const BuyInternationalDrivingPermit = () => {
  const dispatch = useDispatch();
  const { simpleToken } = useAuth0Token();
  const {
    state: { internationalDrivingPermitForm },
  } = useStateMachine({ updateAction });

  const { profile } = useFetchProfile();

  useEffect(() => {
    dispatch(IdpActions.getInternationalDrivingPermit.request(undefined));
  }, [dispatch, simpleToken]);

  useEffect(() => {
    dispatch(idpCountriesAction.getIdpCountries.request(undefined, undefined));
  }, [dispatch]);

  const windowLocation = useWindowLocation();

  // Fetch product article to get dynamicForm
  const { data: productArticle } = useDocumentData(IDP_ARTICLE_SLUG, simpleToken) as {
    data: ProductArticleType & { dynamicForm?: DynamicForm; compareMembershipBlocks?: CompareMembershipBlockType[] };
    meta: {
      isUpdating?: boolean;
      fetchedAt?: string;
      isLoggedin?: boolean;
    };
  };

  const membershipProductData = productArticle?.compareMembershipBlocks?.[0];
  const hasSelectedMembership = !!internationalDrivingPermitForm?.membership?.productId;

  const steps: Step[] = [
    { step: DrivingPermit, name: 'Type førerkort', type: 'FORM' },
    { step: Holder, name: 'Personalia', type: 'FORM' },
    { step: DocumentUpload, name: 'Vedlegg', type: 'FORM' },
    ...(hasSelectedMembership ? [{ step: IdpConsents, name: 'Samtykker', type: 'FORM' } as Step] : []),
    { step: 'Betaling', name: 'Betaling', type: 'REDIRECT' },
  ];

  return (
    <LayoutWithMainContent
      title="Bestill Internasjonalt førerkort"
      description="Her kan du bestille internasjonalt førerkort"
      url={windowLocation}
      gtmArgs={{
        page_type: 'Bestill Internasjonalt førerkort',
        pageCategory: '',
        contentId: 'buy-internation-driving-permit',
      }}
      internalSearchMetaData={{
        cardType: InternalSearchCardType.Simple,
      }}
      isHiddenFromSearch
    >
      <Grid>
        <GridCol s="12" m="12" l="12" xl="12">
          <S.Heading variant={TextVariant.Header1}>Bestill Internasjonalt førerkort</S.Heading>
        </GridCol>
        <GridCol s="12" m="12" l="12" xl="12">
          <StepWizard transitions={{}} isLazyMount nav={<Progressbar steps={steps} />}>
            <DrivingPermit />
            <Holder profile={profile} membershipProductData={membershipProductData} />
            <DocumentUpload dynamicForm={productArticle?.dynamicForm} />
            {hasSelectedMembership && <IdpConsents />}
            <Redirect />
          </StepWizard>
        </GridCol>
      </Grid>
    </LayoutWithMainContent>
  );
};
